import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { AppI18nProvider } from "@app/i18n";
import { Toaster, DownloadTray } from "@app/ui";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { DesktopTitleBar } from "./components/DesktopTitleBar";
import { OfflineToast } from "./components/OfflineToast";
import { registerServiceWorker } from "./lib/RegisterSw";
import { initInstallPrompt } from "./lib/InstallPrompt";
import { initAnalytics } from "./lib/Analytics";
import { detectPlatform } from "./lib/Platform";
import { scheduleSplashDeadline } from "./splash/SplashController";
import { withChunkRetry } from "./lib/ChunkRetry";
import { sessionKeys } from "./queries/Session";

// nightly 채널은 크롬 콘솔을 숨긴다(prod 에 가까운 프리릴리스라 콘솔을 깨끗하게 유지).
// 빌드 시 terser 가 우리 소스의 console 호출을 제거하지만(vite.config.ts), vendor
// (node_modules) 내부에서 console 을 참조형으로 쓰는 잔존 호출은 못 지운다 — 런타임에서
// console 메서드를 no-op 으로 덮어 그 출력까지 막는다. 에러 수집은 PostHog capture_exceptions
// (window.onerror/unhandledrejection 후킹)라 console 과 무관하게 계속 동작한다. develop 은
// 콘솔을 그대로 둬 dev 서버처럼 디버깅하고, prod/dev 는 기존 동작을 유지한다. 어떤 부수효과
// (registerServiceWorker/initAnalytics)보다 먼저 실행되도록 import 직후 최상단에 둔다.
if (import.meta.env.VITE_PUBLIC_APP_ENV === "nightly") {
  /* eslint-disable no-console -- nightly 콘솔 숨김: console 메서드를 의도적으로 no-op 으로 덮는다 */
  console.log =
    console.warn =
    console.error =
    console.info =
    console.debug =
    console.trace =
      (): void => {};
  /* eslint-enable no-console */
}

// Capacitor(android/ios)/Electron(desktop) 셸 전용 네이티브 통합. 웹이 아닐 때만
// lazy 마운트해 @capacitor/* 플러그인과 리마인더에 딸리는 tasks 쿼리(dexie)가
// 웹 entry(초기) 청크에 들어오지 않게 한다(components/PlatformBridge.tsx 참고).
const PlatformBridge = lazy(withChunkRetry(() => import("./components/PlatformBridge")));

// 렌더와 별개로 최대한 빨리 시작 — 첫 로드는 아직 캐시가 없어 못 돕지만, 다음
// 방문부터 앱 셸을 즉시 서빙하려면 등록이 빠를수록 좋다.
void registerServiceWorker();

// beforeinstallprompt 는 React 마운트 전에 발생할 수 있으므로 리스너를 부팅 시점에
// 등록해 이벤트를 놓치지 않는다(웹 컨텍스트에서만 의미 있음). 실제 노출 판정과
// 배너 렌더는 InstallPromptController 가 한다.
if (detectPlatform() === "web") initInstallPrompt();

// PostHog(자체 /ingest 프록시 경유). 첫 페이지뷰/오토캡처를 놓치지 않도록 렌더
// 전에 1회 — dev 빌드나 키 미설정 환경에서는 조용히 no-op 이다(lib/Analytics.ts).
initAnalytics();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1분
      retry: 1,
    },
  },
});

// 새로고침 후에도 세션(로그인된 기기) 목록만 남겨서 매번 리페치/로딩을 안 보여준다.
// 다른 쿼리(tasks 등)까지 통째로 localStorage에 넣으면 크기가 커져 동기 직렬화가
// 메인 스레드를 막을 수 있으니, sessions 쿼리만 골라서 persist한다.
const persister = createSyncStoragePersister({ storage: window.localStorage });

// index.html 에 인라인으로 그려지는 앱 셸 스플래시(#app-splash)가 "먼저 페인트"되도록
// React 마운트를 한 프레임 뒤로 미룬다. 그러지 않으면 entry 실행(React 트리 마운트)이
// 첫 페인트 전 하나의 긴 동기 태스크로 이어져, 스플래시가 이미 DOM 에 있는데도 실제
// 화면이 마운트될 때까지(관측상 ~2.6s, 렌더 블로킹 리소스 없이) 화면이 텅 빈 채로 남는다.
// 더블 requestAnimationFrame 은 브라우저가 스플래시를 한 번 그린 다음 콜백을 실행하도록
// 보장한다(단일 rAF 는 같은 프레임의 페인트 앞에서 실행될 수 있다). 스플래시가 이미
// 즉시 로딩 상태를 보여주므로, 마운트가 한 프레임 늦어져도 체감 지연은 없다.
requestAnimationFrame(() =>
  requestAnimationFrame(() => {
    createRoot(document.getElementById("root")!).render(
      <StrictMode>
        {/* 로그인/온보딩은 라우터 진입 전에 렌더링되므로(App.tsx) i18n Provider 가
            라우터보다 위, 앱 전체를 감싼다. */}
        <AppI18nProvider>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: 5 * 60 * 1000,
              // 캐시된 데이터의 shape이 바뀌면 올려서 옛 캐시를 버린다. 안 버리면 이전
              // 배포에서 저장된 sessions 항목이 새 코드로 복원돼 필드가 어긋난다.
              buster: "sessions-v2",
              dehydrateOptions: {
                shouldDehydrateQuery: (query) => query.queryKey[0] === sessionKeys.all[0],
              },
            }}
          >
            <DesktopTitleBar />
            <OfflineToast />
            {/* App 은 로그인/온보딩/라우터 등 앱의 모든 화면을 렌더하므로 여기서
                바운더리로 감싸 렌더 에러를 폴백으로 잡는다. 타이틀바·오프라인 토스트·
                Toaster 는 밖에 둬서 App 이 죽어도 창 크롬은 남는다. */}
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
            {detectPlatform() !== "web" && (
              <Suspense fallback={null}>
                <PlatformBridge />
              </Suspense>
            )}
            <Toaster position="top-right" />
            <DownloadTray />
          </PersistQueryClientProvider>
        </AppI18nProvider>
      </StrictMode>
    );

    // 앱 셸 스플래시(#app-splash 오버레이)는 #root 밖이라 위 render 로는 지워지지 않는다.
    // 걷는 시점은 이제 "실제 화면의 마운트"가 결정한다(App.tsx 의 SplashDismissOnMount) —
    // 세션 판정·lazy 청크 로딩이 끝나 보여줄 진짜 화면이 준비될 때까지 로고 스플래시가
    // 유지돼, 첫 페인트 직후 걷어 로고 없는 로더로 이어지던 이중 로더를 없앤다. 여기서는
    // 세션 판정이 영영 안 끝나는 극단에 대비한 안전 데드라인만 건다(SplashController).
    scheduleSplashDeadline();
  })
);
